import { useQuery } from 'react-query';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { clientPortalAPI } from '../../api';
import { getEffectiveEstimateStatus } from '../../lib/estimateStatus';
import { FileText, ExternalLink, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const formatCurrency = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n)
    : '—';

const statusBadgeClass = (effective) => {
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ';
  switch (effective) {
    case 'draft':
      return base + 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    case 'sent':
      return base + 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200';
    case 'accepted':
      return base + 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
    case 'rejected':
    case 'declined':
      return base + 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200';
    case 'converted':
      return base + 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200';
    case 'expired':
      return base + 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100';
    default:
      return base + 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  }
};

const statusDisplayLabel = (effective) => {
  if (effective === 'rejected' || effective === 'declined') return 'Rejected';
  if (!effective) return '—';
  return effective.charAt(0).toUpperCase() + effective.slice(1);
};

export default function ClientEstimates() {
  const { data: estimates = [], isLoading, error } = useQuery({
    queryKey: ['client-portal', 'my-estimates'],
    queryFn: () => clientPortalAPI.getMyEstimates(),
  });

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            Your Estimates
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            View your estimates
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-white dark:bg-gray-800">
          <CardContent className="p-0">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
              </div>
            )}
            {error && (
              <div className="p-6 text-center text-red-600 dark:text-red-400">
                Failed to load estimates. Please try again later.
              </div>
            )}
            {!isLoading && !error && estimates.length === 0 && (
              <div className="p-12 text-center text-[var(--ds-text-secondary)]">
                <FileText className="mx-auto h-12 w-12 text-[var(--ds-text-muted)] mb-3" />
                <p className="text-body">You have no estimates yet.</p>
              </div>
            )}
            {!isLoading && !error && estimates.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Estimate
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Reference
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Issued
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Expiry
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Status
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Total
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {estimates.map((est) => {
                      const raw = String(est.status || '').toLowerCase();
                      const normalizedStatus =
                        raw === 'declined' ? 'rejected' : est.status;
                      const effective = getEffectiveEstimateStatus({
                        status: normalizedStatus,
                        expiryDate: est.expiryDate,
                      });
                      return (
                      <tr
                        key={est.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      >
                        <td className="px-4 py-3 text-body font-medium text-[var(--ds-text-primary)]">
                          {est.estimateNum}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {est.reference || '—'}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {est.dateIssued
                            ? format(new Date(est.dateIssued), 'dd MMM yyyy')
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {est.expiryDate
                            ? format(new Date(est.expiryDate), 'dd MMM yyyy')
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={statusBadgeClass(effective)}>
                            {statusDisplayLabel(effective)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-primary)]">
                          {formatCurrency(est.total)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <a
                            href={`${baseUrl}/portal/estimate/${est.viewToken}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <ExternalLink className="h-4 w-4" />
                            View
                          </a>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
